import { useEffect, useRef, useState } from 'react';
import { IconCheck, IconCopy, IconLink, IconShare3 } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { notifications } from '../../lib/notifications';
import { CARD, useNumFmt } from './contestUi';

// Invite card: the owner's referral link (copy / share) and how the invites are doing.
// A friend who joins through the link while the contest runs earns the owner a % of every payment
// (capped per payment) — registering alone earns nothing. Hidden when the admin sets that % to 0.

export default function ContestReferralCard({ data }) {
    const { t } = useTranslation();
    const fmt = useNumFmt();
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const { rules, points, referral } = data;
    if (!(rules.referralPaymentPercent > 0)) return null;
    const paidFriends = Array.isArray(referral.paid) ? referral.paid : [];

    const link = `${window.location.origin}/register?ref=${referral.code}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            notifications.show({ message: t('contest.earn.copied'), color: 'green' });
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 2200);
        } catch {
            notifications.show({ message: link, color: 'blue' });
        }
    };
    const shareNative = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: 'SohozKaj', text: t('contest.earn.shareText'), url: link }); return; } catch { /* cancelled */ }
        }
        copyLink();
    };
    const canShare = typeof navigator !== 'undefined' && !!navigator.share; // phones: the system share sheet

    return (
        <section className={`${CARD} flex flex-col gap-4 p-[22px]`}>
            <div className="flex min-w-0 flex-col gap-0.5">
                <div className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t('contest.earn.referralTitle')}</div>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">{t('contest.earn.referralHint', { n: fmt(rules.referralPaymentPercent), max: fmt(rules.referralMaxPoints ?? 75), laterPercent: fmt(rules.referralLaterPercent ?? 50), laterMax: fmt(rules.referralLaterMaxPoints ?? 35) })}</div>
            </div>

            {/* Link */}
            <div className="flex h-12 min-w-0 items-center gap-2.5 rounded-[12px] border border-zinc-200 bg-zinc-50 pl-3.5 pr-1.5 dark:border-zinc-700 dark:bg-zinc-800">
                <IconLink size={16} className="flex-none text-zinc-400" />
                <div className="min-w-0 flex-1 truncate font-mono text-sm text-zinc-700 dark:text-zinc-200" title={link}>{link.replace(/^https?:\/\//, '')}</div>
                {canShare && (
                    <button type="button" onClick={shareNative} title={t('contest.earn.share')} aria-label={t('contest.earn.share')} className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-[9px] border border-zinc-200 bg-white text-zinc-600 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800">
                        <IconShare3 size={16} />
                    </button>
                )}
                <button type="button" onClick={copyLink} className="inline-flex h-9 flex-none items-center gap-1.5 rounded-[9px] bg-zinc-900 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-700 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-300">
                    {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}{t(copied ? 'contest.earn.copiedShort' : 'contest.earn.copyShort')}
                </button>
            </div>

            {/* Results of the invites */}
            <div className="grid grid-cols-2 gap-2 border-t border-zinc-100 pt-3.5 dark:border-zinc-800">
                <div className="flex flex-col">
                    <div className="font-baloo text-[22px] font-bold leading-[1.2] text-zinc-900 dark:text-zinc-100">{t('contest.earn.joinedCount', { n: fmt(referral.referredInContest ?? 0) })}</div>
                    <div className="text-[13px] text-zinc-500 dark:text-zinc-400">{t('contest.earn.joined')}</div>
                </div>
                <div className="flex flex-col">
                    <div className="font-baloo text-[22px] font-bold leading-[1.2] text-doc-blue-700 dark:text-doc-blue-300">{fmt(points?.referral ?? 0)}</div>
                    <div className="text-[13px] text-zinc-500 dark:text-zinc-400">{t('contest.earn.refPoints')}</div>
                </div>
            </div>

            {/* Friends who joined through the link AND paid (phone is masked by the server) */}
            <div className="flex flex-col gap-2 border-t border-zinc-100 pt-3.5 dark:border-zinc-800">
                <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('contest.earn.paidFriendsTitle')}</div>
                {paidFriends.length === 0 ? (
                    <div className="text-[13px] text-zinc-500 dark:text-zinc-400">{t('contest.earn.paidFriendsEmpty')}</div>
                ) : (
                    <ul className="flex flex-col gap-1.5">
                        {paidFriends.map((f, i) => (
                            <li key={`${f.phone}-${i}`} className="flex items-center gap-3 rounded-[12px] border border-zinc-100 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
                                {f.avatar ? (
                                    <img src={f.avatar} alt="" className="h-9 w-9 flex-none rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                                ) : (
                                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary dark:bg-primary/20">
                                        {(f.name || '?').trim().charAt(0).toUpperCase() || '?'}
                                    </span>
                                )}
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <span className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{f.name || t('contest.earn.friendNoName')}</span>
                                    {f.phone && <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400" dir="ltr">{f.phone}</span>}
                                </div>
                                <span className="flex-none text-sm font-bold text-emerald-700 dark:text-emerald-400">+{fmt(f.points)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
